import React, { useEffect, useState } from "react";
import { AdminSkeleton } from "@/components/FeedSkeleton";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Bot, Sparkles, BookOpen, Activity, Trash2, Users, Loader2, RefreshCw, Gauge, Clock, Shuffle, ShieldCheck, AlertTriangle, FileX } from "lucide-react";

// Active hours/day for a start/end window (end may be 24 = midnight).
const activeHours = (start, end) => {
  start = Math.max(0, Math.min(24, start ?? 0));
  end = Math.max(0, Math.min(24, end ?? 24));
  if (start === 0 && end >= 24) return 24;
  if (end > start) return end - start;
  if (end < start) return 24 - start + end;
  return 24;
};
// Rough daily volume for a cadence preset (reactions are free — not counted).
const estimateCalls = (presets, cadence, start, end, groups) => {
  const cad = (presets || {})[cadence];
  if (!cad) return null;
  const hours = activeHours(start, end);
  const touches = (hours * 60) / Math.max(1, cad.interval_min);
  const posts = touches * (cad.post_prob ?? 1) * (cad.posts ?? 1);
  const comments = touches * (cad.comments ?? 0);
  const groupMsgs = groups ? touches * (cad.group_prob ?? 0) * (cad.group_msgs ?? 0) : 0;
  const llm = posts + comments + groupMsgs + 1;
  return {
    hours, interval_min: cad.interval_min,
    touches: Math.round(touches), posts: Math.round(posts), comments: Math.round(comments),
    groupMsgs: Math.round(groupMsgs), llm: Math.round(llm),
  };
};

export const AiPanel = ({ isSuper, canManage = true }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [meta, setMeta] = useState({ personas: [], system: [], content_types: [] });
  // Create-accounts modal state
  const [seedOpen, setSeedOpen] = useState(false);
  const [seedMode, setSeedMode] = useState("accounts_content");
  const [seedPersonas, setSeedPersonas] = useState(new Set());
  const [seedContent, setSeedContent] = useState(new Set(["verse", "posts", "images", "stories"]));
  // Delete-accounts modal state
  const [delOpen, setDelOpen] = useState(false);
  const [delPersonas, setDelPersonas] = useState(new Set());
  const [delIncludeSystem, setDelIncludeSystem] = useState(false);
  const [delContent, setDelContent] = useState(true);
  const [postsOpen, setPostsOpen] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/admin/ai/status"); setStatus(data); }
    catch { toast.error("Erreur de chargement du statut IA"); }
    setLoading(false);
  };
  const loadMeta = async () => {
    try {
      const { data } = await api.get("/admin/ai/personas");
      setMeta(data);
      setSeedPersonas(new Set(data.personas.map((p) => p.key)));
      setSeedContent(new Set(data.content_types.map((c) => c.key)));
    } catch { /* non-blocking */ }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); loadMeta(); }, []);

  // Poll a few times after a background action so counts refresh.
  const pollAfter = () => {
    let n = 0;
    const iv = setInterval(async () => { n += 1; await load(); if (n >= 6) clearInterval(iv); }, 6000);
  };

  const confirmSeed = async () => {
    if (seedPersonas.size === 0) { toast.error("Sélectionne au moins un persona."); return; }
    setBusy("seed");
    try {
      const body = { mode: seedMode, personas: Array.from(seedPersonas) };
      if (seedMode === "accounts_content") body.content = Array.from(seedContent);
      const { data } = await api.post("/admin/ai/seed", body);
      setSeedOpen(false);
      toast.success(seedMode === "accounts_content"
        ? `${data.total} comptes IA prêts — génération du contenu en cours…`
        : `${data.total} comptes IA prêts (${data.created} créés, ${data.updated} mis à jour).`);
      loadMeta(); pollAfter();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur (réservé au super admin)"); }
    setBusy("");
  };
  const verseNow = async () => {
    setBusy("verse");
    try { await api.post("/admin/ai/verse-now"); toast.success("Verset du jour en cours de publication…"); pollAfter(); }
    catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    setBusy("");
  };
  const activityNow = async () => {
    setBusy("activity");
    try { await api.post("/admin/ai/activity-now"); toast.success("Activité IA en cours de génération…"); pollAfter(); }
    catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    setBusy("");
  };
  const toggleAuto = async (v) => {
    setStatus((s) => ({ ...s, enabled: v }));
    try { const { data } = await api.patch("/admin/ai/settings", { enabled: v }); setStatus(data); }
    catch (e) { setStatus((s) => ({ ...s, enabled: !v })); toast.error(e?.friendlyMessage || "Erreur"); }
  };
  const setCadence = async (cadence) => {
    const prev = status?.cadence;
    setStatus((s) => ({ ...s, cadence }));
    try { const { data } = await api.patch("/admin/ai/settings", { cadence }); setStatus(data); toast.success("Cadence mise à jour"); }
    catch (e) { setStatus((s) => ({ ...s, cadence: prev })); toast.error(e?.friendlyMessage || "Erreur"); }
  };
  const setFlag = async (key, v) => {
    const prev = status?.[key];
    setStatus((s) => ({ ...s, [key]: v }));
    try { const { data } = await api.patch("/admin/ai/settings", { [key]: v }); setStatus(data); }
    catch (e) { setStatus((s) => ({ ...s, [key]: prev })); toast.error(e?.friendlyMessage || "Erreur"); }
  };
  const setNum = async (key, v) => {
    const prev = status?.[key];
    setStatus((s) => ({ ...s, [key]: v }));
    try { const { data } = await api.patch("/admin/ai/settings", { [key]: v }); setStatus(data); toast.success("Horaires mis à jour"); }
    catch (e) { setStatus((s) => ({ ...s, [key]: prev })); toast.error(e?.friendlyMessage || "Erreur"); }
  };
  const setRange = async (start, end) => {
    const prev = { active_start: status?.active_start, active_end: status?.active_end };
    setStatus((s) => ({ ...s, active_start: start, active_end: end }));
    try { const { data } = await api.patch("/admin/ai/settings", { active_start: start, active_end: end }); setStatus(data); toast.success("Horaires mis à jour"); }
    catch (e) { setStatus((s) => ({ ...s, ...prev })); toast.error(e?.friendlyMessage || "Erreur"); }
  };
  const set247 = (v) => (v ? setRange(0, 24) : setRange(8, 22));
  const openDelete = () => {
    setDelPersonas(new Set(meta.personas.filter((p) => p.seeded).map((p) => p.key)));
    setDelIncludeSystem(false);
    setDelContent(true);
    setDelOpen(true);
  };
  const confirmDelete = async () => {
    if (!delIncludeSystem && delPersonas.size === 0) { toast.error("Sélectionne au moins un compte à supprimer."); return; }
    setBusy("delete");
    try {
      const body = { include_system: delIncludeSystem, delete_content: delContent };
      if (!delIncludeSystem) body.personas = Array.from(delPersonas);
      const { data } = await api.post("/admin/ai/delete-bots", body);
      setDelOpen(false);
      toast.success(`${data.deleted_users} comptes IA supprimés${delContent ? ` (${data.deleted_posts} publications)` : ""}.`);
      await load(); loadMeta();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    setBusy("");
  };
  const confirmDeletePosts = async () => {
    setBusy("posts");
    try {
      const { data } = await api.post("/admin/ai/delete-posts", {});
      setPostsOpen(false);
      if (data.started) {
        toast.success(`Suppression de ${data.posts_targeted} publication(s) IA lancée en arrière-plan — l'application reste fluide pendant le nettoyage.`);
      } else {
        toast.success("Aucune publication IA à supprimer.");
      }
      await load(); loadMeta();
      setTimeout(() => { load(); loadMeta(); }, 5000);
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    setBusy("");
  };
  const toggleSet = (setter, set, key) => {
    const next = new Set(set);
    if (next.has(key)) next.delete(key); else next.add(key);
    setter(next);
  };

  if (loading) return <AdminSkeleton />;
  const s = status || {};
  const est = estimateCalls(s.cadence_presets, s.cadence || "moderate", s.active_start ?? 0, s.active_end ?? 24, s.groups !== false);
  const is247 = (s.active_start ?? 0) === 0 && (s.active_end ?? 24) >= 24;

  return (
    <div className="space-y-4" data-testid="ai-panel">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl grid place-items-center shrink-0" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }}><Bot size={20} /></div>
        <div>
          <h2 className="text-lg font-bold font-display">Assistant IA & comptes bots</h2>
          <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }}>Quatre ambassadeurs IA — un par langue (FR · EN · ES · PT) — publient des posts courts illustrés (image différente à chaque fois), réagissent et commentent uniquement les vrais membres de leur langue (commentaire seulement s'ils sont sûrs d'avoir compris), et s'abonnent à eux. Le compte « Verset du jour » publie chaque matin.</p>
        </div>
      </div>

      {/* AI engine health alert — surfaces LLM failures instead of bots silently going quiet */}
      {s.last_llm_error && (
        <div className="yalola-card p-4 flex items-start gap-3" style={{ borderColor: "#dc2626", background: "rgba(220,38,38,0.06)" }} data-testid="ai-llm-error-banner">
          <AlertTriangle size={18} className="shrink-0 mt-0.5" style={{ color: "#dc2626" }} />
          <div className="text-sm">
            <div className="font-semibold" style={{ color: "#dc2626" }}>
              {s.last_llm_error.kind === "budget" ? "Budget IA épuisé (Universal Key)"
                : s.last_llm_error.kind === "auth" ? "Clé IA invalide ou non autorisée"
                : s.last_llm_error.kind === "rate_limit" ? "Limite de débit IA atteinte"
                : "Le générateur IA a rencontré une erreur"}
            </div>
            <p className="text-xs mt-0.5" style={{ color: "var(--yalola-text-muted)" }}>
              Les comptes IA ne peuvent plus générer de nouveau contenu (le fil semble alors « se répéter »).{" "}
              {s.last_llm_error.kind === "budget"
                ? "Recharge le solde dans Profil → Gérer le plan → Universal Key → Ajouter du solde."
                : "Vérifie la configuration de la clé IA."}{" "}
              Dernière erreur : {s.last_llm_error.at ? new Date(s.last_llm_error.at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}.
            </p>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="yalola-card p-4">
          <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>Comptes IA</div>
          <div className="text-2xl font-bold flex items-center gap-1.5" data-testid="ai-status-bots"><Users size={18} style={{ color: "var(--yalola-primary)" }} /> {s.bots ?? 0}</div>
          <div className="text-[11px] mt-0.5" style={{ color: "var(--yalola-text-muted)" }}>{s.persona_bots ?? 0} membres + verset</div>
        </div>
        <div className="yalola-card p-4">
          <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>Publications IA</div>
          <div className="text-2xl font-bold">{s.bot_posts ?? 0}</div>
          <div className="text-[11px] mt-0.5" style={{ color: "var(--yalola-text-muted)" }}>{s.bot_comments ?? 0} commentaires · {s.bot_reactions ?? 0} réactions</div>
        </div>
        <div className="yalola-card p-4">
          <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>Verset du jour</div>
          <div className="text-sm font-bold mt-1.5" style={{ color: s.verse_today ? "#2D9E68" : "var(--yalola-text-muted)" }}>{s.verse_today ? "Publié ✓" : "Pas encore aujourd'hui"}</div>
        </div>
        <div className="yalola-card p-4">
          <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>Dernière activité</div>
          <div className="text-sm font-bold mt-1.5">{s.last_activity ? new Date(s.last_activity).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}</div>
        </div>
      </div>

      {/* Auto activity toggle */}
      <div className="yalola-card p-4 flex items-center justify-between gap-3">
        <div>
          <div className="font-semibold text-sm flex items-center gap-1.5"><Activity size={15} style={{ color: "var(--yalola-primary)" }} /> Activité IA automatique</div>
          <p className="text-xs mt-0.5" style={{ color: "var(--yalola-text-muted)" }}>Publications, commentaires et réactions générés automatiquement par petites touches fréquentes + verset chaque matin.</p>
        </div>
        <Switch checked={!!s.enabled} onCheckedChange={toggleAuto} disabled={!canManage} data-testid="ai-auto-toggle" />
      </div>

      {/* Cadence selector + daily volume estimate */}
      <div className="yalola-card p-4">
        <div className="font-semibold text-sm flex items-center gap-1.5"><Gauge size={15} style={{ color: "var(--yalola-primary)" }} /> Intensité de l'activité IA</div>
        <p className="text-xs mt-0.5 mb-3" style={{ color: "var(--yalola-text-muted)" }}>Les comptes IA agissent par petites touches fréquentes (au lieu d'une grosse rafale) pour un fil vivant toute la journée. Chaque publication / commentaire / message de groupe = 1 appel au modèle IA — les « j'aime » sont gratuits.</p>
        <div className="grid grid-cols-3 gap-2" data-testid="ai-cadence-group">
          {[
            { key: "light", label: "Léger" },
            { key: "moderate", label: "Modéré" },
            { key: "intense", label: "Intense" },
          ].map((c) => {
            const active = (s.cadence || "moderate") === c.key;
            const pe = estimateCalls(s.cadence_presets, c.key, s.active_start ?? 0, s.active_end ?? 24, s.groups !== false);
            return (
              <button
                key={c.key}
                onClick={() => setCadence(c.key)}
                disabled={!canManage}
                className="rounded-xl border p-3 text-left transition-colors disabled:opacity-60"
                style={{
                  borderColor: active ? "var(--yalola-primary)" : "var(--yalola-border)",
                  background: active ? "var(--yalola-primary-soft)" : "transparent",
                }}
                data-testid={`ai-cadence-${c.key}`}
              >
                <div className="text-sm font-bold" style={{ color: active ? "var(--yalola-primary)" : "var(--yalola-text)" }}>{c.label}</div>
                <div className="text-[11px] mt-0.5" style={{ color: "var(--yalola-text-muted)" }}>{pe ? `1 touche / ${pe.interval_min} min` : ""}</div>
                <div className="text-[11px] mt-0.5 font-semibold" style={{ color: "var(--yalola-primary)" }} data-testid={`ai-cadence-est-${c.key}`}>{pe ? `≈ ${pe.llm} appels IA/j` : ""}</div>
              </button>
            );
          })}
        </div>
        {est && (
          <div className="mt-3 rounded-xl p-3" style={{ background: "var(--yalola-primary-soft)" }} data-testid="ai-estimate">
            <div className="text-xs font-semibold mb-1.5" style={{ color: "var(--yalola-primary)" }}>Estimation quotidienne — réglage actuel ({est.hours}h actives/jour)</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs" style={{ color: "var(--yalola-text)" }}>
              <div><span className="font-bold" data-testid="ai-est-posts">≈ {est.posts}</span> publications</div>
              <div><span className="font-bold" data-testid="ai-est-comments">≈ {est.comments}</span> commentaires</div>
              <div><span className="font-bold" data-testid="ai-est-groups">≈ {est.groupMsgs}</span> msgs de groupe</div>
              <div><span className="font-bold" style={{ color: "var(--yalola-primary)" }} data-testid="ai-est-llm">≈ {est.llm}</span> appels IA/jour</div>
            </div>
            <p className="text-[11px] mt-2" style={{ color: "var(--yalola-text-muted)" }}>+ « j'aime » et commentaires rapides sur les publications des vrais membres (les réactions sont gratuites ; les commentaires varient selon l'activité réelle).</p>
          </div>
        )}
      </div>

      {/* Active hours & lifelike behaviour */}
      <div className="yalola-card p-4">
        <div className="font-semibold text-sm flex items-center gap-1.5"><Clock size={15} style={{ color: "var(--yalola-primary)" }} /> Horaires & comportement des IA</div>
        <p className="text-xs mt-0.5 mb-3" style={{ color: "var(--yalola-text-muted)" }}>Rends l'activité des ambassadeurs plus naturelle : plage horaire et variation des heures de publication. Ils n'interagissent jamais entre eux : uniquement avec les vrais membres de leur langue.</p>
        <div className="flex items-center justify-between gap-3 py-2 border-b" style={{ borderColor: "var(--yalola-border)" }}>
          <div>
            <div className="text-sm font-medium">Actives 24h/24</div>
            <p className="text-[11px] mt-0.5" style={{ color: "var(--yalola-text-muted)" }}>Les comptes IA restent actifs jour et nuit — le fil bouge en continu.</p>
          </div>
          <Switch checked={is247} onCheckedChange={set247} disabled={!canManage} data-testid="ai-active-247" />
        </div>
        {!is247 && (
        <div className="flex items-center justify-between gap-3 py-2 border-b" style={{ borderColor: "var(--yalola-border)" }}>
          <div className="flex items-center gap-2 text-sm">
            <span>Actives de</span>
            <select value={s.active_start ?? 8} disabled={!canManage} onChange={(e) => setNum("active_start", parseInt(e.target.value, 10))}
              className="rounded-lg border px-2 py-1 text-sm disabled:opacity-60" style={{ borderColor: "var(--yalola-border)", background: "transparent" }} data-testid="ai-active-start">
              {Array.from({ length: 24 }).map((_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}h</option>)}
            </select>
            <span>à</span>
            <select value={s.active_end ?? 22} disabled={!canManage} onChange={(e) => setNum("active_end", parseInt(e.target.value, 10))}
              className="rounded-lg border px-2 py-1 text-sm disabled:opacity-60" style={{ borderColor: "var(--yalola-border)", background: "transparent" }} data-testid="ai-active-end">
              {Array.from({ length: 25 }).map((_, h) => <option key={h} value={h}>{h === 24 ? "24h" : String(h).padStart(2, "0") + "h"}</option>)}
            </select>
          </div>
        </div>
        )}
        <div className="flex items-center justify-between gap-3 py-3 border-b" style={{ borderColor: "var(--yalola-border)" }}>
          <div>
            <div className="text-sm font-medium flex items-center gap-1.5"><Users size={14} /> Accueil : suivis par les IA</div>
            <p className="text-[11px] mt-0.5" style={{ color: "var(--yalola-text-muted)" }}>L'ambassadeur de la langue du nouveau membre s'abonne à lui à l'inscription, et rattrape progressivement les membres existants de sa langue (+ notification « untel s'est abonné à vous »).</p>
          </div>
          <Switch checked={s.welcome_follow !== false} onCheckedChange={(v) => setFlag("welcome_follow", v)} disabled={!canManage} data-testid="ai-flag-welcome-follow" />
        </div>
        <div className="flex items-center justify-between gap-3 py-3 border-b" style={{ borderColor: "var(--yalola-border)" }}>
          <div>
            <div className="text-sm font-medium flex items-center gap-1.5"><Shuffle size={14} /> Variation des heures</div>
            <p className="text-[11px] mt-0.5" style={{ color: "var(--yalola-text-muted)" }}>Ajoute un décalage aléatoire aux heures de publication pour un rythme naturel.</p>
          </div>
          <Switch checked={s.jitter !== false} onCheckedChange={(v) => setFlag("jitter", v)} disabled={!canManage} data-testid="ai-flag-jitter" />
        </div>
        <div className="flex items-center justify-between gap-3 py-3">
          <div>
            <div className="text-sm font-medium flex items-center gap-1.5"><BookOpen size={14} /> Publications autour du verset du jour</div>
            <p className="text-[11px] mt-0.5" style={{ color: "var(--yalola-text-muted)" }}>Les IA s'inspirent régulièrement du verset du jour pour un fil plus cohérent.</p>
          </div>
          <Switch checked={s.verse_themed !== false} onCheckedChange={(v) => setFlag("verse_themed", v)} disabled={!canManage} data-testid="ai-flag-verse-themed" />
        </div>
      </div>

      {/* AI feature switches */}
      <div className="yalola-card p-4">
        <div className="font-semibold text-sm flex items-center gap-1.5"><Sparkles size={15} style={{ color: "var(--yalola-primary)" }} /> Fonctionnalités IA</div>
        <p className="text-xs mt-0.5 mb-3" style={{ color: "var(--yalola-text-muted)" }}>Activez ou désactivez chaque comportement des comptes IA.</p>
        <div className="divide-y" style={{ borderColor: "var(--yalola-border)" }}>
          {[
            { key: "show_ai_badge", label: "Badge « IA » sur les publications", desc: "Affiche un badge IA sur les publications des comptes robots (transparence)." },
            { key: "groups", label: "Animer le chat des groupes", desc: "L'ambassadeur de la langue du groupe (langue de son créateur) le rejoint et y écrit des messages." },
            { key: "group_topics", label: "Sujets de discussion (hebdo)", desc: "Une question par semaine dans chaque groupe public, dans sa langue." },
            { key: "stories", label: "Stories de versets", desc: "Les ambassadeurs publient de courtes stories-versets, chacun dans sa langue." },
            { key: "prayer_support", label: "Soutien au mur de prières", desc: "L'ambassadeur de la langue de l'auteur intercède (« Je prie ») sur les nouvelles demandes des vrais membres et laisse, la plupart du temps, un court mot d'encouragement. Jamais de faux témoignage, jamais de demande de prière inventée." },
          ].map((f) => (
            <div key={f.key} className="flex items-center justify-between gap-3 py-3">
              <div>
                <div className="text-sm font-medium">{f.label}</div>
                <p className="text-[11px] mt-0.5" style={{ color: "var(--yalola-text-muted)" }}>{f.desc}</p>
              </div>
              <Switch checked={!!s[f.key]} onCheckedChange={(v) => setFlag(f.key, v)} disabled={!canManage} data-testid={`ai-flag-${f.key}`} />
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="yalola-card p-4 space-y-3">
        <div className="text-sm font-bold">Actions</div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setSeedOpen(true)} disabled={!isSuper || busy === "seed"} className="pill pill-primary" data-testid="ai-seed-btn">
            {busy === "seed" ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Sparkles size={15} className="mr-1.5" />} Créer les comptes IA + contenu
          </Button>
          <Button onClick={verseNow} disabled={busy === "verse" || !canManage} variant="outline" className="pill" data-testid="ai-verse-btn">
            <BookOpen size={15} className="mr-1.5" /> Publier le verset du jour
          </Button>
          <Button onClick={activityNow} disabled={busy === "activity" || !canManage} variant="outline" className="pill" data-testid="ai-activity-btn">
            <RefreshCw size={15} className="mr-1.5" /> Générer de l'activité
          </Button>
        </div>
        <div className="pt-2 border-t" style={{ borderColor: "var(--yalola-border)" }}>
          <Button onClick={openDelete} disabled={!isSuper || busy === "delete"} variant="outline" className="pill text-red-600 border-red-200 hover:bg-red-50" data-testid="ai-delete-btn">
            {busy === "delete" ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Trash2 size={15} className="mr-1.5" />} Supprimer des comptes IA
          </Button>
          <p className="text-[11px] mt-1.5" style={{ color: "var(--yalola-text-muted)" }}>Choisis quels comptes IA supprimer et si tu retires aussi leur contenu. Les comptes système (Verset du jour, Plans de lecture) restent protégés.</p>
          <Button onClick={() => setPostsOpen(true)} disabled={!isSuper || busy === "posts"} variant="outline" className="pill text-red-600 border-red-200 hover:bg-red-50 mt-2" data-testid="ai-delete-posts-btn">
            {busy === "posts" ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <FileX size={15} className="mr-1.5" />} Supprimer uniquement les publications IA
          </Button>
          <p className="text-[11px] mt-1.5" style={{ color: "var(--yalola-text-muted)" }}>Retire seulement les publications (photos/textes/reels) des comptes IA. Leurs commentaires et réactions sur les publications des vrais membres sont <strong>conservés</strong>.</p>
        </div>
        {!isSuper && <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>Seul le super administrateur peut créer/supprimer les comptes IA.</p>}
      </div>

      {/* Create AI accounts modal */}
      <Dialog open={seedOpen} onOpenChange={(v) => { if (!v) setSeedOpen(false); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="ai-seed-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Sparkles size={18} style={{ color: "var(--yalola-primary)" }} /> Créer des comptes IA</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-semibold">Que veux-tu créer ?</Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                {[
                  { k: "accounts", t: "Comptes uniquement", d: "Créer les comptes, sans contenu" },
                  { k: "accounts_content", t: "Comptes + contenu", d: "Créer les comptes et générer du contenu" },
                ].map((m) => (
                  <button key={m.k} onClick={() => setSeedMode(m.k)} className="rounded-xl border p-3 text-left transition-colors"
                    style={{ borderColor: seedMode === m.k ? "var(--yalola-primary)" : "var(--yalola-border)", background: seedMode === m.k ? "var(--yalola-primary-soft)" : "transparent" }}
                    data-testid={`ai-seed-mode-${m.k}`}>
                    <div className="text-sm font-bold" style={{ color: seedMode === m.k ? "var(--yalola-primary)" : "var(--yalola-text)" }}>{m.t}</div>
                    <div className="text-[11px] mt-0.5" style={{ color: "var(--yalola-text-muted)" }}>{m.d}</div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">Ambassadeurs (1 par langue) — {seedPersonas.size}/{meta.personas.length}</Label>
                <button className="text-xs underline" style={{ color: "var(--yalola-primary)" }}
                  onClick={() => setSeedPersonas(seedPersonas.size === meta.personas.length ? new Set() : new Set(meta.personas.map((p) => p.key)))}
                  data-testid="ai-seed-selectall">
                  {seedPersonas.size === meta.personas.length ? "Tout désélectionner" : "Tout sélectionner"}
                </button>
              </div>
              <div className="grid grid-cols-2 gap-1.5 mt-1.5 max-h-48 overflow-y-auto">
                {meta.personas.map((p) => (
                  <label key={p.key} className="flex items-center gap-2 rounded-lg px-2 py-1.5 cursor-pointer text-sm" style={{ background: "var(--yalola-surface-alt)" }} data-testid={`ai-seed-persona-${p.key}`}>
                    <input type="checkbox" checked={seedPersonas.has(p.key)} onChange={() => toggleSet(setSeedPersonas, seedPersonas, p.key)} />
                    <span className="truncate">{p.lang ? <span className="inline-block text-[10px] font-bold uppercase px-1.5 py-0.5 rounded mr-1.5 text-white" style={{ background: "var(--yalola-primary)" }}>{p.lang}</span> : null}{p.name}{p.city ? <span className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}> · {p.city}</span> : null}</span>
                  </label>
                ))}
              </div>
              <p className="text-[11px] mt-1" style={{ color: "var(--yalola-text-muted)" }}>Un ambassadeur par langue : il publie, commente, réagit et s'abonne uniquement dans sa langue (commentaire seulement s'il est sûr d'avoir compris, sinon simple réaction). Les comptes système (Verset du jour, Plans de lecture) sont créés/conservés automatiquement.</p>
            </div>

            {seedMode === "accounts_content" && (
              <div>
                <Label className="text-sm font-semibold">Type de contenu à générer</Label>
                <div className="flex flex-wrap gap-2 mt-1.5">
                  {meta.content_types.map((c) => (
                    <label key={c.key} className="flex items-center gap-2 rounded-full border px-3 py-1.5 cursor-pointer text-sm" style={{ borderColor: seedContent.has(c.key) ? "var(--yalola-primary)" : "var(--yalola-border)" }} data-testid={`ai-seed-content-${c.key}`}>
                      <input type="checkbox" checked={seedContent.has(c.key)} onChange={() => toggleSet(setSeedContent, seedContent, c.key)} />
                      {c.label}
                    </label>
                  ))}
                </div>
                <p className="text-[11px] mt-1.5 flex items-center gap-1" style={{ color: "var(--yalola-text-muted)" }}><AlertTriangle size={12} /> La génération de contenu consomme le crédit IA (clé Universelle).</p>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" className="rounded-full" onClick={() => setSeedOpen(false)}>Annuler</Button>
              <Button className="rounded-full pill-primary" onClick={confirmSeed} disabled={busy === "seed"} data-testid="ai-seed-confirm">
                {busy === "seed" ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : null} Créer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete AI accounts modal */}
      <Dialog open={delOpen} onOpenChange={(v) => { if (!v) setDelOpen(false); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="ai-delete-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2 text-red-600"><Trash2 size={18} /> Supprimer des comptes IA</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="rounded-xl p-3 flex items-start gap-2 text-sm" style={{ background: "var(--yalola-primary-soft)" }}>
              <ShieldCheck size={16} style={{ color: "var(--yalola-primary)" }} className="mt-0.5 shrink-0" />
              <span>Les comptes <strong>système</strong> (Verset du jour, Plans de lecture) sont <strong>protégés</strong> et ne seront pas supprimés — sauf si tu coches l'option ci-dessous.</span>
            </div>

            <div className={delIncludeSystem ? "opacity-40 pointer-events-none" : ""}>
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">Comptes personas à supprimer — {delPersonas.size}</Label>
                <button className="text-xs underline" style={{ color: "var(--yalola-primary)" }}
                  onClick={() => { const seeded = meta.personas.filter((p) => p.seeded).map((p) => p.key); setDelPersonas(delPersonas.size === seeded.length ? new Set() : new Set(seeded)); }}
                  data-testid="ai-del-selectall">
                  {delPersonas.size === meta.personas.filter((p) => p.seeded).length ? "Tout désélectionner" : "Tout sélectionner"}
                </button>
              </div>
              <div className="grid grid-cols-2 gap-1.5 mt-1.5 max-h-44 overflow-y-auto">
                {meta.personas.filter((p) => p.seeded).map((p) => (
                  <label key={p.key} className="flex items-center gap-2 rounded-lg px-2 py-1.5 cursor-pointer text-sm" style={{ background: "var(--yalola-surface-alt)" }} data-testid={`ai-del-persona-${p.key}`}>
                    <input type="checkbox" checked={delPersonas.has(p.key)} onChange={() => toggleSet(setDelPersonas, delPersonas, p.key)} />
                    <span className="truncate">{p.name}</span>
                  </label>
                ))}
                {meta.personas.filter((p) => p.seeded).length === 0 && <p className="text-xs col-span-2" style={{ color: "var(--yalola-text-muted)" }}>Aucun compte persona créé.</p>}
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm cursor-pointer rounded-lg px-3 py-2" style={{ background: "var(--yalola-surface-alt)" }} data-testid="ai-del-content">
              <input type="checkbox" checked={delContent} onChange={(e) => setDelContent(e.target.checked)} />
              <span>Supprimer aussi leur contenu (publications, commentaires, réactions, stories)</span>
            </label>

            <label className="flex items-start gap-2 text-sm cursor-pointer rounded-lg px-3 py-2 border" style={{ borderColor: delIncludeSystem ? "#DC2626" : "var(--yalola-border)" }} data-testid="ai-del-include-system">
              <input type="checkbox" checked={delIncludeSystem} onChange={(e) => setDelIncludeSystem(e.target.checked)} className="mt-0.5" />
              <span className="text-red-600"><strong>Supprimer aussi les comptes système</strong> (Verset du jour, Plans de lecture). À éviter — ces comptes gèrent des fonctions clés.</span>
            </label>

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" className="rounded-full" onClick={() => setDelOpen(false)}>Annuler</Button>
              <Button className="rounded-full text-white" style={{ background: "#DC2626" }} onClick={confirmDelete} disabled={busy === "delete"} data-testid="ai-delete-confirm">
                {busy === "delete" ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Trash2 size={15} className="mr-1.5" />} Supprimer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete AI posts only — keeps accounts + their comments/reactions on real posts */}
      <Dialog open={postsOpen} onOpenChange={(v) => { if (!v) setPostsOpen(false); }}>
        <DialogContent className="max-w-md" data-testid="ai-delete-posts-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2 text-red-600"><FileX size={18} /> Supprimer les publications IA</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p>Cette action supprime <strong>uniquement les publications</strong> (photos, textes, reels) des comptes IA.</p>
            <p style={{ color: "var(--yalola-text-muted)" }}>Les comptes IA restent actifs, et leurs <strong>commentaires et réactions</strong> laissés sur les publications des vrais membres sont <strong>conservés</strong>.</p>
            <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>La suppression s'effectue <strong>en arrière-plan</strong>, par petits lots : l'application reste utilisable pendant le nettoyage.</p>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" className="rounded-full" onClick={() => setPostsOpen(false)}>Annuler</Button>
              <Button className="rounded-full text-white" style={{ background: "#DC2626" }} onClick={confirmDeletePosts} disabled={busy === "posts"} data-testid="ai-delete-posts-confirm">
                {busy === "posts" ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <FileX size={15} className="mr-1.5" />} Supprimer les publications
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AiPanel;
